import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, ButtonAdd } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useGetSupplierQuery } from "@/redux/services/inventory/supplier.api";
import { useGetPurchaseOrderQuery } from "@/redux/services/inventory/purchaseOrder.api";
import {
  Ingredient,
  Supplier,
} from "@/redux/services/inventory/inventory.type";
import {
  useBulkPurchaseOrders,
  emptyOrder,
  MAX_BULK_ORDERS,
  bulkGrandTotalCents,
} from "@/hooks/inventory/useBulkPurchaseOrders";
import { displayPrice } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import BulkSupplierOrderBlock from "./BulkSupplierOrderBlock";
import SupplierFormModal from "@/components/inventory/suppliers/SupplierFormModal";

interface BulkPurchaseOrderFormProps {
  restaurantId: string;
}

const BulkPurchaseOrderForm = ({
  restaurantId,
}: BulkPurchaseOrderFormProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledSupplierId = searchParams.get("supplier");
  const duplicateFromId = searchParams.get("from");
  const currency = useRestaurantCurrency();

  // Caches shared across blocks so labels survive search-result churn
  const supplierCache = useRef<Map<string, Supplier>>(new Map());
  const ingredientCache = useRef<Map<string, Ingredient>>(new Map());

  // Inline supplier creation: remember which block triggered it
  const [supplierModalForBlock, setSupplierModalForBlock] = useState<
    number | null
  >(null);

  const {
    register,
    control,
    errors,
    orderFields,
    appendOrder,
    removeOrder,
    watch,
    setValue,
    onSubmit,
    isLoading,
  } = useBulkPurchaseOrders(restaurantId, () => {
    navigate("/inventory/purchase-orders");
  });

  // Deep-link prefill: seed the first block's supplier
  const { data: prefilledSupplier } = useGetSupplierQuery(
    { supplierUuid: prefilledSupplierId ?? "" },
    { skip: !prefilledSupplierId }
  );

  useEffect(() => {
    if (prefilledSupplier) {
      supplierCache.current.set(prefilledSupplier.id, prefilledSupplier);
      setValue("orders.0.supplier", prefilledSupplier.id, {
        shouldValidate: true,
      });
    }
  }, [prefilledSupplier, setValue]);

  // Duplicate flow: prefill the first block from a source PO
  const { data: sourcePO } = useGetPurchaseOrderQuery(
    { uuid: duplicateFromId ?? "" },
    { skip: !duplicateFromId }
  );
  const hasAppliedDuplicateRef = useRef(false);

  useEffect(() => {
    if (!sourcePO || hasAppliedDuplicateRef.current) return;
    if (sourcePO.supplier?.id) {
      supplierCache.current.set(
        sourcePO.supplier.id,
        sourcePO.supplier as Supplier
      );
      setValue("orders.0.supplier", sourcePO.supplier.id, {
        shouldValidate: true,
      });
    }
    sourcePO.lines.forEach((line) => {
      ingredientCache.current.set(line.ingredient.id, line.ingredient);
    });
    setValue(
      "orders.0.lines",
      sourcePO.lines.map((line) => {
        const usePackaging = !!line.packaging && !!line.packageQuantityOrdered;
        return {
          ingredient: line.ingredient.id,
          packaging: usePackaging ? line.packaging?.id ?? "" : "",
          quantityOrdered: usePackaging
            ? line.packageQuantityOrdered ?? ""
            : line.quantityOrdered,
          unitPrice: usePackaging
            ? ((line.packageUnitPrice ?? 0) / 100).toString()
            : (line.unitPrice / 100).toString(),
        };
      }),
      { shouldValidate: false }
    );
    hasAppliedDuplicateRef.current = true;
  }, [sourcePO, setValue]);

  // Grand total across every supplier block
  const orders = watch("orders") ?? [];
  const grandTotal = bulkGrandTotalCents(orders);

  const handleSupplierCreated = (supplier: Supplier) => {
    supplierCache.current.set(supplier.id, supplier);
    if (supplierModalForBlock !== null) {
      setValue(`orders.${supplierModalForBlock}.supplier`, supplier.id, {
        shouldValidate: true,
      });
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Saisissez vos achats du marché par fournisseur. Tous les bons de
            commande créés ici sont enregistrés comme{" "}
            <strong>reçus</strong> et le stock est mis à jour immédiatement.
          </span>
        </div>

        {orderFields.map((field, index) => (
          <BulkSupplierOrderBlock
            key={field.id}
            index={index}
            restaurantId={restaurantId}
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            supplierCache={supplierCache}
            ingredientCache={ingredientCache}
            onRemove={() => removeOrder(index)}
            canRemove={orderFields.length > 1}
            onCreateSupplier={() => setSupplierModalForBlock(index)}
          />
        ))}

        {typeof errors.orders?.message === "string" && (
          <p className="text-xs text-danger">{errors.orders.message}</p>
        )}

        <div className="flex items-center justify-between gap-2">
          <ButtonAdd
            label="Ajouter un fournisseur"
            type="button"
            disabled={orderFields.length >= MAX_BULK_ORDERS}
            onClick={() => appendOrder(emptyOrder())}
          />
          <div className="text-sm font-medium text-card-foreground">
            Total général :{" "}
            <span className="text-base font-semibold">
              {displayPrice(grandTotal, currency)}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/inventory/purchase-orders")}
          >
            Annuler
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Enregistrer les achats
          </Button>
        </div>
      </form>

      <SupplierFormModal
        isOpen={supplierModalForBlock !== null}
        onClose={() => setSupplierModalForBlock(null)}
        restaurantId={restaurantId}
        onCreated={handleSupplierCreated}
      />
    </>
  );
};

export default BulkPurchaseOrderForm;
